// An event's card (docs/SPEC.md item 4 "Two-level card"; web/sheets.js cardView): the peek (its icon, title, when ·
// distance · how you get in, the small star, ✕) and below it the full card: alerts, the flyer, when (with "End time
// unknown" loudly when nobody knows), where, access and price, recurring, its types (your interests picked out), the
// description, I'm interested, Directions, Not interested, and the source. Desktop shows it all in the side panel;
// at phone width it opens as the peek and drags or taps up to the full card (Sheet.tsx).
import type { FeedEvent } from "../../data/event";
import { distance, imageUrl, schedule, sourceLine, sourceNote } from "../../data/format";
import { T } from "../../shared/config";
import { Icon, type IconName } from "../../shared/icons";
import { RULES } from "../../shared/rules";
import { tagEmoji } from "../../state/feed";
import { useProfileView } from "../../state/profile";
import { closeSheet, openSheet, useView } from "../../state/view";
import { CloseButton } from "../../ui/CloseButton";
import { EmojiBadge } from "../../ui/EmojiBadge";
import { Flyer } from "../../ui/Flyer";
import { TimeText } from "../../ui/TimeText";
import { InterestedButton, NotInterestedButton, StarButton } from "../profile/slots";
import buttons from "../../ui/buttons.module.css";
import styles from "./Card.module.css";

export function Card({ event: e, full }: { event: FeedEvent; full: boolean }) {
  const loc = useView((s) => s.userLocation), interests = useProfileView().interests;
  const place = [e.venue_name, e.address].filter(Boolean).join("\n") || T("card.locationOnMap");
  const dist = distance(e, loc), src = sourceLine(e), note = sourceNote(e), img = imageUrl(e);
  const setFull = (f: boolean) => openSheet({ kind: "event", id: e.id, full: f });
  return (
    <div className={styles.card} id="card" data-full={full || undefined}>
      <button type="button" className={styles.grabber} id="card-expand" aria-label={full ? "Show less" : "Show more"} onClick={() => setFull(!full)} />
      <div className={styles.head} id="card-peek">
        <EmojiBadge event={e} size="normal" />
        <div className={styles.headText}>
          <h2 id="card-title">{e.title}</h2>
          <div className={styles.peekLine} id="peek-line">
            <TimeText event={e} />{[dist, RULES.admission(e)].filter(Boolean).map((x) => ` · ${x}`).join("")}
          </div>
        </div>
        <span className={styles.peekStar}><StarButton event={e} place="peek" /></span>
        <CloseButton onClick={(ev) => { ev.stopPropagation(); closeSheet(); }} />
      </div>
      <div className={styles.full}>
        {e.status === "cancelled" ? <div className={styles.alert} id="card-cancelled"><Icon name="ban" />{T("card.cancelled")}</div>
          : e.availability === "sold_out" ? <div className={styles.alert}>{T("card.soldOut")}</div>
          : e.availability === "closed" ? <div className={styles.alert}>{T("card.closed")}</div> : null}
        {img && <Flyer src={img} id="card-image" className={styles.image} />}
        <div className={styles.rows}>
          <Row icon="calendar" text={schedule(e)}
            detail={RULES.endUnknown(e) ? <span className={styles.endUnknown} id="card-end-unknown">{T("card.endUnknown")}</span> : null} />
          <Row icon="map-pin" text={place} detail={dist && `${dist} away`} />
          <Row icon={RULES.openness(e) === "ticketed" ? "ticket" : "walk"} text={RULES.admission(e)} id="access-line" />
          {e.recurring && <Row icon="repeat" text={T("card.recurring")} />}
        </div>
        {e.tags.length > 0 && (
          <div className={styles.tags}>
            {e.tags.map((t) => (
              <span key={t} className={`${styles.tag}${interests.has(RULES.norm(t)) ? ` ${styles.mine}` : ""}`}>
                {tagEmoji(t) ? `${tagEmoji(t)} ${t}` : t}
              </span>
            ))}
          </div>
        )}
        {e.description && <p className={styles.desc}>{e.description}</p>}
        <div className={styles.actions}><InterestedButton event={e} /></div>
        <div className={styles.actions}>
          <a className={buttons.btn} target="_blank" rel="noopener"
            href={`https://www.google.com/maps/dir/?api=1&destination=${e.lat},${e.lng}&travelmode=walking`}>{T("card.directions")}</a>
          <NotInterestedButton event={e} />
        </div>
        {src && e.source_url ? <a className={styles.source} id="source-link" href={e.source_url} target="_blank" rel="noopener">{src}<Icon name="arrow-up-right" /></a>
          : src ? <div className="muted small">{src}</div> : null}
        {note && <div className="muted small">{note}</div>}
      </div>
    </div>
  );
}

function Row({ icon, text, detail, id }: { icon: IconName; text: string; detail?: React.ReactNode; id?: string }) {
  return (
    <div className={styles.row} id={id}>
      <span className={styles.ic} aria-hidden="true"><Icon name={icon} /></span>
      <div><div style={{ whiteSpace: "pre-line" }}>{text}</div>{detail && <div className={styles.detail}>{detail}</div>}</div>
    </div>
  );
}
